import type { AssetScope } from "../components/gallery/galleryTree";
import type { AssetResponse } from "../generated/api/types.gen";

/** Empty-state copy for the org assets section. */
export const ORG_ASSETS_EMPTY_MESSAGE =
  "No shared assets yet. Drag one of your assets here to share it with the organization.";

/**
 * Splits the merged asset list down to the bucket the current scope
 * renders. Personal assets have ``org_id === null``; org-shared assets
 * have it set. The backend returns one merged list per request and the
 * frontend partitions it at render time.
 *
 * The personal-vs-org bucket rule mirrors the authoritative visibility
 * logic in ``AssetRepository.list_visible`` (Repository/asset_repository.py);
 * keep the two in sync if the rule changes.
 */
export function filterAssetsByScope(
  assets: AssetResponse[],
  scope: AssetScope,
): AssetResponse[] {
  return assets.filter((asset) =>
    scope === "organization" ? asset.org_id !== null : asset.org_id === null,
  );
}

/**
 * Whether the drawer renders its "Shared Projects" section.
 *
 * Clerk auto-creates a personal org for every account at signup, so the
 * presence of an active org says nothing about entitlement — the gate is
 * ``canUseOrgFeatures`` (paid org plan or billing-exempt org), mirroring
 * ``AuthService.get_active_org_context`` on the backend. The name/root clause
 * is defence in depth: a stale query cache shouldn't resurrect the section.
 */
export function shouldShowSharedSection(gallery: {
  canUseOrgFeatures: boolean;
  activeOrgName: string | null;
  orgRoot: unknown | null;
}): boolean {
  return (
    gallery.canUseOrgFeatures &&
    (gallery.activeOrgName !== null || gallery.orgRoot !== null)
  );
}

/**
 * Title shown above the asset grid. Falls back to "My Assets" when the
 * org name isn't available — the org section is hidden in the drawer in
 * that case, so the title is only reached for personal scope.
 */
export function assetSectionTitle(
  scope: AssetScope,
  activeOrgName: string | null,
): string {
  if (scope === "organization" && activeOrgName) {
    return `${activeOrgName}'s Assets`;
  }
  return "My Assets";
}
